import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, MessageSquare, ThumbsDown } from "lucide-react";
import { useChatUI } from "@contexts/ChatContext";
import { useRejectedObservations } from "@features/chat/hooks/useRejectedObservations";
import ObservationFlagIcon, { SEVERITY_BG } from "@ui/ObservationFlagIcon";

/**
 * Red warning triangle in the top-right corner of a container. Click it →
 * centered modal listing the observation(s) with full text. The modal
 * renders via a portal so it escapes the container's bounds (otherwise
 * small stat cards squish it into a narrow vertical sliver).
 *
 * The visual (icon + count pill) is delegated to ObservationFlagIcon so
 * the Dashboard corner badge, Budget row flags, and Timeline phase flags
 * all render identically.
 */

export default function ObservationCornerBadge({ observations, containerLabel }) {
  const [open, setOpen] = useState(false);
  const { seedAndOpen } = useChatUI();
  const { reject } = useRejectedObservations();
  const modalRef = useRef(null);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!observations || observations.length === 0) return null;

  const discuss = (obs) => {
    setOpen(false);
    seedAndOpen({
      topic: {
        title: obs.title,
        body: obs.body,
        containerLabel,
        severity: obs.severity,
        category: obs.category,
      },
    });
  };

  const onReject = async (obs) => {
    try {
      await reject(obs);
      // Observation disappears from the filtered list (useLatestAIReview).
      // If this was the last one, close the modal.
      if (observations.length <= 1) setOpen(false);
    } catch (err) {
      console.error("[ObservationCornerBadge] reject failed", err);
    }
  };

  const modal = open
    ? createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center p-4 pointer-events-none"
          style={{ zIndex: 2147483647 }}
          onClick={() => setOpen(false)}
        >
          <div
            ref={modalRef}
            onClick={(e) => e.stopPropagation()}
            className="rounded-lg w-full max-w-5xl max-h-[85vh] flex flex-col pointer-events-auto"
            style={{
              background: "var(--bg-root)",
              border: "1px solid var(--border-card)",
              boxShadow:
                "0 24px 60px rgba(0,0,0,0.55), 0 6px 20px rgba(0,0,0,0.35)",
            }}
          >
            <header
              className="flex items-center justify-between px-4 py-2.5 flex-shrink-0"
              style={{ borderBottom: "1px solid var(--border-card)" }}
            >
              <div className="flex items-center gap-3 min-w-0">
                <p
                  className="text-[10px] uppercase tracking-wider font-semibold flex-shrink-0"
                  style={{ color: "var(--text-secondary)" }}
                >
                  AI observations
                </p>
                <p
                  className="text-xs font-semibold truncate"
                  style={{ color: "var(--text-heading)" }}
                >
                  {containerLabel}
                </p>
                <span
                  className="text-[10px] flex-shrink-0"
                  style={{ color: "var(--text-secondary)" }}
                >
                  {observations.length} item{observations.length > 1 ? "s" : ""}
                </span>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-1 rounded hover:bg-black/10 transition-colors flex-shrink-0"
                style={{ color: "var(--text-secondary)" }}
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </header>

            <div className="overflow-y-auto p-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                {observations.map((obs, i) => (
                  <div
                    key={i}
                    className="rounded-md p-2.5 flex flex-col"
                    style={{
                      background: "var(--bg-card)",
                      border: "1px solid var(--border-card)",
                    }}
                  >
                    <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                      <span
                        className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded"
                        style={{
                          background: SEVERITY_BG[obs.severity] ?? SEVERITY_BG.info,
                          color: "white",
                        }}
                      >
                        {obs.severity}
                      </span>
                      <span
                        className="text-[10px] uppercase tracking-wider font-semibold"
                        style={{ color: "var(--text-secondary)" }}
                      >
                        {obs.category}
                      </span>
                    </div>
                    <p
                      className="text-sm font-semibold mb-1.5 leading-snug"
                      style={{ color: "var(--text-heading)" }}
                    >
                      {obs.title}
                    </p>
                    <p
                      className="text-sm leading-relaxed mb-2.5 flex-1"
                      style={{ color: "var(--text-body)" }}
                    >
                      {obs.body}
                    </p>
                    <div className="flex items-center gap-1.5 self-start">
                      <button
                        onClick={() => discuss(obs)}
                        className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold rounded-md text-white bg-indigo-600 hover:bg-indigo-500 transition-colors"
                      >
                        <MessageSquare className="w-3 h-3" />
                        Discuss
                      </button>
                      <button
                        onClick={() => onReject(obs)}
                        className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold rounded-md transition-colors"
                        style={{
                          background: "rgba(239,68,68,0.12)",
                          border: "1px solid rgba(239,68,68,0.4)",
                          color: "#ef4444",
                        }}
                        title="Dismiss permanently — Gemini won't suggest this again"
                      >
                        <ThumbsDown className="w-3 h-3" />
                        Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className="absolute -top-2 -right-2 hover:scale-110 transition-transform z-10"
        aria-label="Show AI observations"
      >
        <ObservationFlagIcon observations={observations} size="md" />
      </button>
      {modal}
    </>
  );
}
